import { DEFAULT_FILTERS, SORT_OPTIONS, SUPPORTED_LEGACY_PARAMS, URL_PARAMS } from './settings'
import { float, rangeListOf } from '../../utility/serializers'
import { addressReducer } from '../../utility/state'
import { history } from '../../services'
import { hydrateState } from '../../integrations/hydration'
import { DEFAULT_PAGE_SIZE } from '../../settings'
import { setMapViewport } from '@bikemap/js/integrations/map/actions'

history.configure({
    queryParamTypes: {
        ...URL_PARAMS,
        ...SUPPORTED_LEGACY_PARAMS,
    },

    queryParamDefaults: {
        ...DEFAULT_FILTERS,
        page: 1,
        sort: SORT_OPTIONS[0].key,
        pageSize: DEFAULT_PAGE_SIZE,
    },
})

/**
 * Read query parameters and update state.
 * @param {import("redux").Store} store
 */
export function hydrateFromQueryParams(store) {
    history.hydrateState(params => { mapParamsToState(store, params) })
}

/**
 * Setup bindings between state and history.
 * @param {import("redux").Store} store
 */
export function initHistory(store) {
    history.bindState(store, mapStateToParams)
}

/**
 * Map the current global state to URL parameters.
 * @param {import("./store").SearchState} state
 * @returns {Object} Unserialized URL parameters
 */
function mapStateToParams({ results, mapViewport, geolocation, filters, primaryFilters }) {
    if (!primaryFilters) primaryFilters = {}
    const { page, pageSize, sort, order } = results
    const sortOption = SORT_OPTIONS.find(option => option.sort === sort && option.order === order)

    return {
        ...filters,
        ...primaryFilters,
        page,
        center: mapViewport.center,
        zoom: mapViewport.zoom,
        location: geolocation.location,
        sort: sortOption && sortOption.key,

        // Include page size if needed
        pageSize: (page > 1) ? pageSize : null,
    }
}

/**
 * Hydrate the state according to the current URL parameters.
 * @param {import("redux").Store} store
 * @param {Object} params
 */
function mapParamsToState(store, params) {
    dispatchHydration(store, {
        isLegacyLink: hasLegacyParam(params),
        primaryFilters: getPrimaryFiltersFromParams(params),
        filters: getFiltersFromParams(params),
    })

    hydrateMapViewportFromParams(store, params)
    hydrateListFromParams(store, params)
    hydrateGeolocationFromParams(store, params)
}

/**
 * @param {Object} params
 */
function getPrimaryFiltersFromParams(params) {
    const { relativeAscent, relativeDescent } = params

    const legacyToMinMax = (min, max) => rangeListOf(float, 0, 100).deserialize((min || 0) + ',' + max)

    const primaryFilters = removeEmpty({
        relativeAscent: relativeAscent || (
            (params.ascent_min || params.ascent_max) && legacyToMinMax(params.ascent_min, params.ascent_max)
        ),
        relativeDescent: relativeDescent || (
            (params.descent_min || params.descent_max) && legacyToMinMax(params.descent_min, params.descent_max)
        ),
    })

    return Object.keys(primaryFilters).length ? primaryFilters : null
}

/**
 * @param {Object} params
 */
function getFiltersFromParams(params) {
    const { q, distance, ascent, categories, surfaces, onlyLoop } = params

    const filters = removeEmpty({
        q,
        distance,
        ascent,
        categories,
        surfaces,
        onlyLoop: (
            onlyLoop ||
            ((params.only_loop === 'true') && true) ||
            ((params.only_loop === 'false') && false) ||
            null
        ),
    })

    return Object.keys(filters).length ? filters : null
}

/**
 * @param {import("redux").Store} store
 * @param {Object} params
 */
function hydrateMapViewportFromParams(store, { center, zoom }) {
    if (center && zoom) {
        store.dispatch(setMapViewport({ center, zoom }))
    }
}

/**
 * @param {import("redux").Store} store
 * @param {Object} params
 */
function hydrateListFromParams(store, params) {
    const { page, pageSize, sort } = params

    const sortOption = sort && SORT_OPTIONS.find(option => option.key === sort)

    dispatchHydration(store, {
        page: page || 1,
        pageSize,
        sort: (
            (sortOption && sortOption.sort) ||
            // Handle legacy param value
            (sort === 'ascend' && 'ascent') ||
            // Handle legacy rating_min as sort by rating
            (params.rating_min && 'rating')
        ),
        order: sortOption && sortOption.order,
    }, 'results')
}

/**
 * @param {import("redux").Store} store
 * @param {Object} params
 */
function hydrateGeolocationFromParams(store, { location }) {
    dispatchHydration(store, { location }, 'geolocation')
}

/**
 * @param {import("redux").Store} store
 * @param {Object} stateUpdate Changes that should be made to the state
 * @param {string} [reducerKey] Key of a reducer (sub-state) to hydrate
 */
function dispatchHydration(store, stateUpdate, reducerKey) {
    const cleanedStateUpdate = removeEmpty(stateUpdate)

    if (Object.keys(cleanedStateUpdate).length) {
        let action = hydrateState(cleanedStateUpdate)
        if (reducerKey) {
            action = addressReducer(reducerKey, action)
        }

        store.dispatch(action)
    }
}

/**
 * @param {Object} obj
 * @returns {Object} A new object with only the defined (and not null) values
 */
function removeEmpty(obj) {
    const cleaned = {}
    for (const key in obj) {
        if (obj[key] !== undefined && obj[key] !== null) {
            cleaned[key] = obj[key]
        }
    }
    return cleaned
}

/**
 * @param {Object} params Object with param values
 * @returns {boolean} Whether the params object contains a legacy key
 */
function hasLegacyParam(params) {
    const legacyParamKeys = Object.keys(SUPPORTED_LEGACY_PARAMS)
    for (const paramKey in params) {
        if (legacyParamKeys.includes(paramKey)) {
            return true
        }
    }
    return false
}
